/**
 * @copyright 2023 Honda Motor Co., Ltd. All rights reserved.
 */

import { Box, styled } from "@mui/material";
import { Const } from "const";
import { FC, ReactNode } from "react";
import { TaskRegisterPanelHeader } from "../Common/TaskRegisterPanelHeader";
import { TaskControlState, headerHeight } from "./consts";
import { TaskRegisterPanelFooter } from "components/Common/TaskRegisterPanelFooter";
import { PanelContentsArea } from "./Util";

export interface ButtonContext {
  text: string;
  action: () => void;
}

interface Props {
  state: TaskControlState;
  frameTypeAndNumber?: string;
  canForward?: boolean;
  forwardButtonContext?: ButtonContext;
  backButtonContext?: ButtonContext;
  children?: ReactNode;
  interruptButtonContext?: ButtonContext;
}
export const TaskControlPanel: FC<Props> = ({
  state,
  frameTypeAndNumber,
  canForward,
  forwardButtonContext,
  backButtonContext,
  children,
  interruptButtonContext,
}: Props) => {
  return (
    <Container>
      <HeaderArea>
        <TaskRegisterPanelHeader
          titles={getPhaseTitles(state)}
          phase={getPhase(state)}
          frameTypeAndNumber={frameTypeAndNumber}
          onInterrupt={interruptButtonContext?.action}
        />
      </HeaderArea>
      <PanelContentsArea>{children}</PanelContentsArea>
      <TaskRegisterPanelFooter
        leftLabel={backButtonContext?.text ?? ""}
        leftHandle={
          backButtonContext?.action ??
          (() => {
            // default no-op.
          })
        }
        rightLabel={forwardButtonContext?.text}
        rightHandle={
          forwardButtonContext?.action ??
          (() => {
            // default no-op.
          })
        }
        disabled={canForward !== undefined ? !canForward : false}
      />
    </Container>
  );
};

/**
 * Get titles of control phase
 * @param{TaskControlState} state Control state
 * @return{string[]} Phase titles
 */
function getPhaseTitles(state: TaskControlState): string[] {
  // If count of phase is 3, just return
  if (state.processTitles.length === 3) {
    return state.processTitles;
  }
  return state.phase > 0
    ? state.processTitles.slice(1) // "Select Point" > "Setting" > "Confirm"
    : [state.processTitles[0], ...state.processTitles.slice(2)]; // "Select Task" > "Setting" > "Confirm"
}

/**
 * Get current phase number
 * @param{TaskControlState} state Control state
 * @return{number} Current phase number
 */
function getPhase(state: TaskControlState): number {
  // If count of phase is 3, just return
  if (state.processTitles.length === 3) {
    return state.phase;
  }
  return state.phase > 1 ? state.phase - 1 : 0;
}

const Container = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  width: "100%",
  height: "100%",
}));

const HeaderArea = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  height: headerHeight,
  width: "100%",
  backgroundColor: Const.sidePanelHeaderColor,
}));
